import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePhotoCompleteTask, newIdempotencyKey, familyKeys } from '@kidshome/api-contract';
import { invalidateHeroSurfaces } from '@/entities/hero';

/**
 * `photo-complete-task` — the FIRST completion of a virtual PHOTO-PROOF card (ADR-0142 F8). Because the
 * card has no occurrence id yet, the image + completion travel together in one multipart step
 * (`usePhotoCompleteTask`). Unlike the plain `complete-task` hook there is no optimistic flip: the photo
 * has to reach the server before the chore is really `pending_approval`, so we simply invalidate the
 * day / missions / balance / week / quest / hero surfaces on settle and let the server reconcile — the
 * capture UI shows its own pending state meanwhile.
 */
export interface PhotoCompleteContext {
  readonly date: string;
  readonly assigneeId: string;
  readonly childId?: string;
}

export function usePhotoCompleteTaskAction(ctx: PhotoCompleteContext): {
  /** Complete a photo-proof card with a processed image `File`; resolves once the request settles. */
  submit: (
    card: { templateId: string; scheduledLocalDate: string; assigneeId: string },
    file: File,
  ) => Promise<void>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const childId = ctx.childId ?? ctx.assigneeId;
  const mutation = usePhotoCompleteTask();

  const submit = useCallback(
    (card: { templateId: string; scheduledLocalDate: string; assigneeId: string }, file: File) =>
      new Promise<void>((resolve, reject) => {
        mutation.reset();
        mutation.mutate(
          {
            optimisticId: `virtual:${card.templateId}:${card.scheduledLocalDate}`,
            templateId: card.templateId,
            localDate: card.scheduledLocalDate,
            assigneeId: card.assigneeId,
            file,
            idempotencyKey: newIdempotencyKey(),
          },
          {
            onSuccess: () => resolve(),
            onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
            onSettled: () => {
              void qc.invalidateQueries({ queryKey: familyKeys.day(ctx.assigneeId, ctx.date) });
              void qc.invalidateQueries({ queryKey: familyKeys.missions(ctx.assigneeId) });
              void qc.invalidateQueries({ queryKey: familyKeys.balance(childId) });
              void qc.invalidateQueries({ queryKey: familyKeys.weeks(ctx.assigneeId) });
              void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
              invalidateHeroSurfaces(qc);
            },
          },
        );
      }),
    [mutation, qc, ctx.assigneeId, ctx.date, childId],
  );

  return { submit, isPending: mutation.isPending, error: mutation.error };
}
